export default {
	activity: {
		create: "New activity",
		edit: "Edit activity",
		name: "Activity name",
		dataobject: "Linked dataobject",
		dataobjectHint: "The dataobject this activity acts on.",
		actions: {
			empty: {
				title: "No actions yet",
				hint: "Drag an element from the Elements panel onto the canvas to add an action.",
			},
		},
		empty: {
			title: "No activities yet",
			hint: "Create an activity to model an operation on a dataobject.",
		},
		// The groups of a type picker: an input's, an output's and an event's.
		typePicker: {
			primitives: "Basic types",
			arrays: "Lists",
			types: "Types",
			dataobjects: "Dataobjects",
			builtIn: "Built-in",
		},
		canvas: {
			removeConnection: "Remove connection",
			zoomIn: "Zoom in",
			zoomOut: "Zoom out",
			fitView: "Fit everything into view",
			// A value a node shows with its field's name, as "Attribute: street".
			labelledValue: "{label}: {value}",
		},
		// The overview's tag groups: named sets of tags it groups its activities by, the project's.
		tagGroups: {
			groupBy: "Group by",
			groupNone: "None",
			groupTags: "Tag groups",
			groupDataobject: "Dataobject",
			edit: "Edit groups",
			collapseAll: "Collapse all",
			expandAll: "Expand all",
			// The section of activities no group takes.
			loose: "Not in a group",
			countOf: "{count} of {total}",
			empty: "No activity has one of these tags yet.",
			// On a card in a group: the other groups it shows under.
			alsoIn: "Also in {groups}",
			noMatchIn: "No match in {sections}.",
			noneYet: "No tag groups yet",
			noneYetHint:
				"A group gathers every activity that has one of its tags. Everyone on the project sees the same groups.",
			title: "Tag groups",
			note: "An activity shows in every group one of its tags belongs to. Drag a group to change the order.",
			none: "No groups yet.",
			name: "Name",
			nameEmpty: "Give the group a name.",
			nameTaken: "Another group has this name.",
			// A group's tags no activity carries (any more). Kept, so the group does not change by itself.
			unused: "No activity has: {tags}",
			count: "No activities | 1 activity | {count} activities",
			remove: "Remove {name}",
			unnamed: "unnamed group",
			add: "New group",
			leftOver: "Tags in no group",
			leftOverHint: "Drag one onto a group, or press it.",
			leftOverHintNoGroups: "Drag one onto “New group”, or press it.",
			// A tag in "Tags in no group", as its button names it; it opens the menu of groups.
			addTag: "Add {tag} to a group (1 activity) | Add {tag} to a group ({count} activities)",
			newWithTag: "New group with this tag",
			loadFailed:
				"The tag groups could not be loaded, so the list cannot be grouped by them.",
		},
		noResults: "No activities match your search.",
		dataobjectRequired: "Pick a dataobject.",
		deleteConfirm: "Delete activity “{name}”?",
		deletedRemotely: "This activity was deleted by another user.",
		// The name a duplicated action is given. Persisted, so it is written in the
		// language of whoever pressed duplicate — free text either way, and easier to
		// rename than to explain.
		copyName: "{name} copy",
		duplicateShortcut: "Duplicate the selected action",

		import: {
			open: "Import from another activity",
			title: "Import an action from another activity",
			search: "Search actions and activities…",
			loading: "Loading the project's activities…",
			empty: "No other activity in this project has actions yet.",
			noMatches: "No action matches your search.",
			blockedHeading: "Not for this dataobject",
			// Why a row can't be picked, naming the attribute it wants — which is what tells
			// you whether to add it here or leave the action where it is.
			blocked: "needs {attribute}",
		},

		// The element types the palette offers, by display name. A new node's NAME is the registry's
		// English `label`, persisted — these only label the type wherever it is shown.
		elements: {
			categories: {
				tasks: "Tasks",
				data: "Data",
				events: "Events",
				alternis: "Alternis",
				telephony: "Telephony",
				time: "Time",
				process: "Process",
				interactables: "Interactables",
			},
			types: {
				CUSTOM_ACTION: "Custom Action",
				CLIENT_SCRIPT: "Client Script",
				ATTRIBUTE_LISTENER: "Attribute Listener",
				ATTRIBUTE_WRITER: "Attribute Writer",
				EVENT_LISTENER: "Event Listener",
				EVENT_EMITTER: "Event Emitter",
				LIFECYCLE_HOOK: "Lifecycle Event",
				ALTERNIS_SEND_MESSAGE: "Alternis: Send Message",
				ALTERNIS_SEND_PREFAB: "Alternis: Send Prefab",
				ALTERNIS_ON_SESSIONS: "Alternis: Session Events",
				ALTERNIS_ON_MESSAGES: "Alternis: Message Events",
				EMIT_NOTIFICATION: "Show Notification",
				EMIT_ATTRIBUTE_SUGGESTION: "Show Attribute Suggestion",
				EMIT_TAB: "Show Tab",
				EMIT_ALTERNIS_CHAT: "Show Alternis Chat",
				EMIT_QUESTION: "Ask Question",
				EMIT_QUESTION_TAB: "Show Question Tab",
				EMIT_PANEL: "Show Panel",
				REMOVE_QUESTION_TAB: "Remove Question Tab",
				SET_QUESTION_PRIORITY: "Set Question Priority",
				LOG: "Log",
				CONFIGURE_SUGGESTIONS: "Configure Suggestions",
				VOICEBOT: "Voicebot",
				SIP_ON_CALLS: "SIP: Call Events",
				SIP_CALL_CONTROL: "SIP: Call Control",
				DATAOBJECT_OPERATION: "Dataobject Operation",
				TICKER: "Ticker",
				TIMER: "Timer",
				ON_TIME: "At a Time",
				SPAWN_PROCESS: "Start Process",
				SEND_EVENT_TO_PROCESS: "Send Event to Process",
				PROCESS_CONTROL: "Process Control",
				LIST_PROCESSES: "List Processes",
				PROCESS_ACCESS: "Share Process",
				LIST_USERS: "List Users",
			},
			// The Elements panel's tiles that create a type preconfigured (ActionPreset).
			presets: {
				graphly: "Graphly",
				lumina: "Lumina",
				question_list: "Question list",
				suggestion_pool: "Suggestion pool",
			},
		},

		sidebar: {
			// A checkbox for the actions that tell the Operator when they fail (`NotifyOnFailureField`).
			notifyOnFailure: {
				title: "When it fails",
				label: "Show a notification",
				hint: "Off: nothing is shown, and the failure is for the model to handle — the output's ok is false and its error says why.",
			},
			usages: {
				title: "Used elsewhere",
				here: "In this activity",
				noneEvent: "No other action emits or listens to this event.",
				noneAttribute: "No other action writes, watches, reads or suggests this attribute.",
				badge: {
					listeners: "Heard by {n} listener | Heard by {n} listeners",
					emitters: "Sent by {n} emitter | Sent by {n} emitters",
				},
				codeNotRead: "The code of custom actions is not searched.",
				roles: {
					emits: "emits",
					listens: "listens",
					/* An attribute is watched, not listened to: an ATTRIBUTE_LISTENER's row. */
					watches: "watches",
					writes: "writes",
					reads: "reads",
					suggests: "suggests",
				},
			},
			elements: "Elements",
			searchElements: "Search elements…",
			readOnlyHint: "You can view this activity but not change it.",
			// A writer a question keeps in sync with one of its attribute-assigned inputs.
			managedHint:
				"Created for the question “{question}”. Change what it writes on the question; here you can rename, describe, move and recolour it.",
			// A VOICEBOT's outputs after the state: what it decided, for the process to act on — and the
			// session it talks in, which is no decision but an id to keep.
			voicebotEventsHint:
				"The outputs from “handoff” to “failed” each report something the voicebot decided. Wiring “handoff” replaces the operator notification: what it is wired to takes the call over. “session” is the Alternis session the voicebot talks in: wire it to an Attribute Writer for a text attribute named session_id, which every Alternis action reads by default.",
			configuration: "Configuration",
			optionalLabel: "{label} (optional)",
			clearColor: "Clear",
			known: "Used here:",
			groups: {
				content: "Content",
				placement: "Placement",
				appearance: "Appearance",
				behaviour: "Behaviour",
				actors: "Who may act",
				session: "Session",
				chat: "Chat",
				voice: "Voice",
				telephony: "Telephone call (SIP)",
				location: "Location service",
				gateway: "LLM gateway",
				advanced: "Advanced",
			},
			iconPicker: {
				empty: "No icon matches.",
				clear: "Clear {label}",
			},
			poolEntries: {
				kind: "What the card stands for",
				attribute: "Attribute",
				question: "Question field",
				remove: "Remove card",
				attributeLabel: "Attribute",
				attributePlaceholder: "Select attribute…",
				questionLabel: "Question ID",
				questionPlaceholder: "e.g. ask-name",
				inputLabel: "Input name",
				inputPlaceholder: "e.g. name",
				before: "Show before a suggestion exists",
				after: "Keep after its value was taken",
				title: "Title",
				titlePlaceholder: "Left empty, the operator names it",
				icon: "Icon",
				incomplete: "Choose what this card stands for — until then it is not saved.",
				repeated: "Another card stands for the same thing.",
				none: "No cards — the pool shows only the suggestions that are open.",
				add: "Add card",
			},
			chatRoles: {
				role: "Role",
				label: "Name",
				labelPlaceholder: "e.g. Dispatch",
				icon: "Icon",
				color: "Colour",
				clearColor: "Use the chat's own colour",
				remove: "Remove role",
				numberInvalid: "A role number is a whole number from 0 to 255.",
				repeated: "This role is already named.",
				incomplete: "Enter a number and a name — until then this role is not saved.",
				none: "No roles are named — the chat uses its own words for them.",
				add: "Add role",
				addOwn: "Add the bot and the caller",
			},
			hints: {
				suggestionValue:
					"What the suggestion proposes: the value of an input or one of its fields, a fixed value, an environment variable or an attribute. Left empty, it proposes its first input's value.",
				poolEntries:
					"Cards the pool keeps whether or not a suggestion exists — an attribute, or a field of a question — in the order shown here. A card can be shown empty before a suggestion arrives, and kept once its value was taken.",
				chatRoles:
					"What the chat calls each role number, the icon beside it and the colour its messages are shown in. Role 0 is the dispatcher — on a voicebot, the bot — and the other numbers are whoever the session says. A role without a name is shown as “Dispatcher” or “Caller”, and one without a colour in the chat's own colour for its number.",
				emitImmediately:
					"When the spoken version of the message is ready, it plays straight away. Off: someone starts it from the chat.",
				freezeText:
					"Shown text normally follows the attributes it reads. Freeze it to keep the text as it was when shown.",
				interactableId:
					"Identifies what is shown, so a later action can update or remove it. Showing the same ID again updates it in place.",
				notificationTitle:
					"A few words over the body, saying what it is about. Left empty, the body stands alone.",
				notificationBody: "Left empty, the notification shows its first input's value.",
				notificationSeverity:
					"How the operator sees it: below 2 is information, 2 and 3 a warning, 4 and up an error. An error is shown before anything else that is waiting.",
				tabAddress:
					"What the tab opens. Left empty, the tab shows its first input's value.",
				onDismiss:
					"Whether a viewer can remove it, with the ✕ beside it. Default: a tab or notification can be removed, a panel or chat cannot. Yes, remove it: removed for everyone. Yes, fire the output: the action gets a “dismissed” output, and it stays until another action removes it. A removed chat stays gone while its session lasts.",
				panelColumn:
					"Which column of the operator it is shown in. Two panels in the same column and alignment are shown as tabs, and a column with nothing in it takes no room.",
				panelAlign:
					"Whether it is shown at the top or the bottom of its column. Alone in its column, it fills it.",
				chatTitle:
					"What the chat is called where it shares its place with another panel, as a tab: a fixed text, an attribute or an environment variable. Left empty, it is named after its session.",
				panelTitle: "Left empty, the operator names the panel itself.",
				panelId:
					"Left empty, the panel is panel::graphly, panel::lumina or panel::question_list — the ID a legacy process's own panel has, so this retitles that one instead of adding a second. Two suggestion pools need an ID each, or the second moves the first.",
				voiceProvider:
					"Preferred for the text messages this action sends — the operator's, and a voicebot's own speech. Alternis falls back to another provider when this one has no voice for the language. A prefab cannot carry a voice.",
				voiceInstructions:
					"How the line should be delivered, e.g. “speak calmly and slowly”. VoxCPM only. May read attributes, not inputs: it is read each time a message is sent.",
				voiceMode:
					"How a question is spoken when it sets none of its own. Scripted: its text, word for word. Guided: the LLM phrases it, covering every open field. Free: the LLM may talk with the caller while it is open and steer back. Default: scripted.",
				offPath:
					"What the bot does when the caller says something that is not an answer, for a question that sets none of its own. Acknowledge: a few words, then it asks again. Answer: the LLM answers from the system prompt, then returns to the question. Handoff: the operator is notified — or, with the voicebot's “handoff” output wired, what it is wired to — and the question set aside. Default: acknowledge.",
				floorPolicy:
					"Who may speak while the bot does. Open: the caller is never muted, so a correction mid-sentence is heard. Half duplex: muted only while the bot's own line plays. Strict: muted whenever the bot is busy, and unmuted only while it listens. Default: open.",
				silenceTimeout:
					"How long, in milliseconds, the bot listens before it nudges: it asks again, or moves on from a question it has asked enough times. Empty is 8000; 0 turns the nudge off.",
				reaskMax:
					"How many times a question is asked, the first included, before it is set aside and the bot moves on. A question's own setting wins. Empty is 2.",
				greeting:
					"Said once at the start, before any question. May read attributes, not inputs: it is read when the bot speaks.",
				closing:
					"Said when nothing is left to ask — everything answered, set aside or optional. May read attributes, not inputs.",
				questionScope:
					"Restricts the bot to the questions shown under this question tab. Empty is every question; set it when two voicebots share a process.",
				questionVoiceText:
					"What the voicebot says, when the body is markdown written for the screen. Left empty, it speaks the body without its markdown.",
				questionVoiceMode:
					"Default: the voicebot's own Voice mode. Free also answers the caller when they stray, unless the question hands them to the operator.",
				questionOffPath:
					"What the voicebot does when the caller says something that is not an answer. Default: the voicebot's own setting.",
				questionReaskMax:
					"How many times the voicebot asks this question before setting it aside. Empty: the voicebot's own setting.",
				questionReaskRephrase:
					"Whether asking again is phrased differently by the LLM, or repeated word for word. Default: rephrased unless the question is scripted.",
				locationService:
					"The location-matching service that answers a Location input. What is set here beats NOTITIA_LOCATION_SERVICE_URL. It needs a running Alternis chat — an “Alternis: Message Events” action, a chat in session mode, or a voicebot — to have anything to match.",
				locationRegion:
					"Optionally restricts the service to one region, named exactly as the service has it — Saarland, Thueringen, Baden-Wuerttemberg. A name it does not know restricts nothing. A Location input can set its own.",
				locationPhone:
					"A phone number sent with every request. The service ranks addresses near its area code higher and never filters by it. It wins over the caller's number a voicebot sends. The number is personal data.",
				suggestionsGateway:
					"The LLM gateway that derives suggestions from the conversation. What is set here beats the environment, which beats a voicebot's own gateway.",
				suggestionsEnabled:
					"Unchecked turns automatic suggestions off. Left checked, the environment decides (NOTITIA_NO_AUTO_SUGGESTIONS).",
				questionTab:
					"The question tab this question appears under. Left empty, it is under the default tab.",
				questionGroup:
					"Questions with the same group are shown together. Left empty, they group by their dataobject; “on-top” shows the group above all others.",
				questionPriority:
					"Higher is shown first; empty is 0. From an attribute, the question re-orders when it changes.",
				questionTabId:
					"Questions name this ID as their tab. “default” restyles the default tab.",
				removeQuestionTab: "The default tab cannot be removed.",
				chatSessionId:
					"The Alternis session the chat follows and shows. Empty, the chat stays empty until a session is set — from an attribute, as soon as it has one.",
				setQuestionPriority:
					"Overrides that question's own priority while it is shown. A question that is not shown is left alone.",
				sipNumbers:
					"Only calls to these numbers reach this action: separate them with commas or spaces, and end one with * to match any rest (+4930123456*). Empty: every call to the dataspace. The trunk's own numbers, in the dataspace's Telephony settings, decide what reaches the dataspace at all.",
				sipAction:
					"What this action does each time its inputs are complete. “hangup” ends a call, or rejects one nobody has claimed yet; “transfer” hands a call on to another number; “call” dials out. The settings below follow from it.",
				sipCallId:
					"The call to act on. An input carries the whole value its source sent, so the incoming call of a “SIP: Call Events” action arrives as the call, not as its ID: read the ID off it (callId) in a custom action first.",
				sipNumber:
					"The number to dial or hand the call on to: a + and up to 15 digits (+4930123456). Left empty, the extension below is used.",
				sipExtension:
					"An extension on the trunk's coms system, used when the number is empty.",
				sipReference:
					"Free text, up to 128 characters, that the coms system receives with the call.",
				sipStatus:
					"The SIP status an unanswered call is rejected with, from 300 to 699 — 486 means busy, 603 means decline. Empty is 486. A call that has been answered is ended whatever this says.",
				voicebotSessionId:
					"The Alternis session the bot talks in; without a call it is required. On a call through Notitia, leave it empty: the bot creates the session itself and reports it on its “session” output. On a call through the coms system, name the coms system's session.",
				voicebotCallId:
					"The telephone call this bot answers. Setting it makes this a bot for a call: it waits for the call's ID, then answers the call and talks on it. Under the Notitia integration the Session ID above becomes optional — the bot creates the Alternis session itself. Read the ID from an input to take it from the incoming call of a “SIP: Call Events” action (one process can be both), or from an environment variable when that action starts a process for each call. The inputs that drive the call, hangup, transfer and call, are added on the Wiring tab.",
				alternisIntegration:
					"How the call gets into Alternis. Coms system: the coms system already put it there, and the bot uses the Session ID above. Notitia: the bot creates the session itself and the server carries the audio. Auto: whatever the trunk says in its Telephony settings. Default: auto.",
				bargeIn:
					"What the caller's speech does to a line the bot is saying. Stop: it is cut off once the caller has spoken for the grace time. Off: a line is always finished. With a half duplex or strict floor policy choose Off — the caller is muted in Alternis while the bot speaks, so the line would be stopped for speech nobody hears. Default: stop.",
				bargeInGraceMs:
					"How long, in milliseconds, the caller must speak before it counts as interrupting. Empty is 500. No effect while barge-in is off.",
				bargeInDuck:
					"Turns the line down to about a third while the caller starts to speak, before it counts as interrupting. Speech that stops within the grace time puts it back; nothing is lost.",
				bargeInMinWords:
					"With a number above 0 the line is only turned down, and the bot stops it once the caller's live transcript holds this many words: “yes” does not cut it off, “no, wait a moment” does. Empty is 0: the grace time decides.",
				cueThinking:
					"Loops a quiet sound while the bot has nothing to say yet, so the caller does not hear silence. Unchecked turns it off.",
				cueListening:
					"A short sound when the bot hands the floor to the caller. Off unless checked.",
				cueMute:
					"Sounds when the floor mutes and unmutes the caller — the half duplex and strict floor policies. Off unless checked.",
				transferTarget:
					"Where a transfer goes when the input that fires it carries no target — a handoff output wired straight to it, say: a number (+4930123456) or an extension on the trunk's coms system.",
				locationCallerNumber:
					"Sends the caller's phone number to the location service with each Location suggestion of the call, which then ranks addresses near its area code higher. It is personal data, so it is off unless checked. Mobile and foreign numbers have no effect. A phone number set in Configure Suggestions wins.",
				pathPrefix:
					"A path of attributes, such as address.street. Hears that attribute and everything under it, whole names only: address hears address.street, not addressbook. Empty: every path.",
				changeKinds: "Empty: every kind of change.",
				excludeOrigins:
					"Changes made by these are not heard. Leave it empty to hear them all; pick “An axiom” to ignore what axioms infer.",
				dataobjectOperation:
					"What the action does to dataobjects each time one of its inputs fires. “done” carries the ID of the dataobject, “failed” the reason it was refused.",
				operationConcept:
					"The kind of dataobject to create. It is created like the process's entry for that kind.",
				operationTemplate:
					"Which entry of the process to make it like, by its name. Empty: the process's first entry for the dataobject.",
				operationPath:
					"Which optional or list-valued embedded dataobject to fill, as a path through single embedded dataobjects.",
				operationRelation: "One of the relations of the dataobject to act on.",
				operationTargetId:
					"The ID of the dataobject to link or unlink, usually read from an input.",
				operationData:
					"The values the new dataobject starts with, as an object. Applied before anything else hears of it.",
				operationOwnerId:
					"The dataobject to act on. Empty: the dataobject this activity is on.",
				operationOwnerConcept:
					"The concept of the dataobject named above, which the relation or slot is checked against.",
				tickerInterval:
					"Milliseconds between ticks, at least 10. The first tick comes after one interval, and the output is the count, from 1.",
				tickerLimit: "Stop after this many ticks. Empty: never stop on its own.",
				timerTimeout:
					"Milliseconds until it fires. With nothing wired to start it, it starts when the process does; a process that is stopped and continued keeps the deadline and fires at once if it passed.",
				timerRetrigger:
					"What starting it does to a timer that is already running. A new value on an input always starts it again from now.",
				timerPassthrough:
					"When on, the timer hands on the value its Start input was given once the time is up, instead of nothing: a delay. Give the Start input the value's type to read its fields afterwards. A process that is stopped meanwhile keeps the value.",
				onTimeSchedule:
					"Once: at a single date and time. Daily: every day at a time, on the days you choose.",
				onTimeAt:
					"A date and time with no offset is read in the time zone below. Empty: it never fires.",
				onTimeTime: "The time of day, read in the time zone below.",
				onTimeWeekdays: "Empty: every day.",
				onTimeTimezone:
					"The zone the time is read in, taken from this browser when the action was added. Default: the server's own.",
				spawnDefinition:
					"The process to start, each time one of the action's inputs fires. The process this one belongs to may be chosen too, if its grants allow it. The output carries the new process, or the reason it was refused.",
				spawnName: "What the new process is called in the Operator. Empty: its own name.",
				spawnDescription: "The new process's description in the Operator.",
				spawnRestartRule:
					"What happens when the new process stops. The Operator can change it later. Default: only when a person asks.",
				spawnSeed:
					"The dataobjects the new process starts with, the first time it starts only. Switch the value to an expression and write a JSON list of objects with a concept, an optional template and data. It can also be read from an input or an attribute.",
				sendProcessId:
					"One process, by its instance ID, usually read from an input. Set this or the definition below, not both.",
				sendDefinition:
					"Every running process of this definition. Set this or an instance ID, not both.",
				sendChannel:
					"The name of the event. A listener in the other process hears it by this name. Names reserved for internal use (such as ones that start with cb_ or attr_) are refused.",
				sendData: "What to send with the event. Empty: nothing.",
				processOperation:
					"What the action does to a process each time one of its inputs fires. Stopping, restarting, archiving and deleting itself needs no grant; acting on another process does.",
				controlTarget:
					"The ID of the process to act on, usually read from an input. Empty: this process itself.",
				actors: "Only these people may answer it, press its buttons, close it or use its controls — a NotitiaUser, a list of them, a user ID or an email, usually read from an attribute. Empty: everybody who may take part in the process. Set but naming nobody, for instance before an owner is chosen: nobody.",
				accessOperation:
					"Share: give a person access to this process, beyond their role. Stop sharing: take what they were given away. Allowed when the person who started this process may share processes; otherwise the output says it was refused.",
				accessUser:
					"Who: a user ID or an email address, or a NotitiaUser — read from an input, an attribute or a question's answer. Only somebody who can reach the dataspace can be given access.",
				accessGrant:
					"Can view: the process is in their list and opens. Can view and take part: they may also answer its questions and change its data.",
				listDefinition: "Only processes of this definition.",
				listStatus:
					"Only processes in these states. Empty: every state, archived ones included.",
				listArchived:
					"On: only archived processes. Off: only the others. Left unset: both.",
				listSearch: "Text in the process's name or description, without regard to case.",
				listCreatedBy:
					"Only processes started by this user ID. A process started by another process inherits the user ID of the one that started it.",
				listSpawnedBy: "Only processes that this process ID started.",
				listInstant:
					"A date and time with its offset, like 2026-03-29T01:30:00Z. The start is included and the end is not.",
				listSort: "What the list is ordered by.",
				listOrder: "Which way the list is ordered.",
				listLimit: "At most this many processes, from 1 to 1000.",
				listOffset: "Skip this many before the first one, for the next page.",
				onTimeMissed:
					"What to do about a time that came while the process was stopped. A process that was never stopped has missed nothing.",
			},
			template: {
				insert: "Insert value",
				insertHint: "Insert a value — or type {'{'} in the text",
				placeholder: "Type text; {'{'} inserts a value",
				placeholderMultiline: "Type the text; {'{'} inserts a value",
				groups: {
					attr: "Attributes",
					inp: "Inputs",
					env: "Environment",
				},
				kinds: {
					attr: "Attribute",
					inp: "Input",
					env: "Environment variable",
				},
				envCustom: "Environment variable {name}",
				noMatches: "Nothing matches. Keep typing, or press Esc.",
				expression:
					"This field holds a TypeScript expression — the old way of putting values into text. It still works, but can only be edited as text again once replaced.",
				replaceExpression: "Replace with text",
				brokenAttribute: "There is no attribute {name} on this dataobject any more",
				brokenInput:
					"{name} is not an input with a source — connect it, or remove this value",
				brokenField: "{input} has no field {field} — pick another, or remove this value",
			},
			required: "required",
			moreActions: "More actions",
			// Named at the top of the panel while a dropped node waits for its binding.
			requiredFields: "{fields} is required. | {fields} are required.",
			sessionModeConditions:
				"A chat takes no conditions: it follows its session. Saving removes any that are set.",
			// A folded settings section with nothing set in it.
			sections: {
				nothingSet: "Nothing set",
			},
			tabs: {
				label: "Action sections",
				settings: "Settings",
				question: "Question",
				answers: "Answers",
				wiring: "Wiring",
			},
			deleteNodeConfirm: "Delete “{name}”?",
			deleteNodeNote: "Its connections are removed with it.",
			// Shared by the question answer rows and the var fields.
			unrecognizedValue: "unrecognized value",

			identity: {
				name: "Name",
				description: "Description",
				descriptionPlaceholder: "Enter description…",
				descriptionIconHint:
					"An mdi-… icon name written here becomes the action's icon; the rest is shown on the action.",
			},

			attribute: {
				clear: "Clear attribute",
				write: "Attribute to write to",
				listen: "Attribute to listen to",
				placeholder: "Select attribute…",
				// The tree picker: why an embedded list cannot be picked, and the empty search.
				arrayNotSelectable: "list of {type} — not selectable",
				noMatches: "No attribute matches.",
				notifyOnFailure: "Notify on rejected write",
				listenerPort: "Its output carries the attribute's type.",
				writerPort: "Its input takes the attribute's type.",
				notifyOnFailureHint:
					"When checked, a rejected write (invalid value, or an axiom guard) also shows a notification. The write always broadcasts on this action's own error channel regardless of this setting.",
			},

			event: {
				name: "Name",
				emitted: "Event to emit",
				listened: "Event to listen for",
				namePlaceholder: "Event name…",
				nameNotSaved: "Saved once the event has a type.",
				type: "Event type",
				typePlaceholder: "Select type…",
				sender: "Also output who sent the event",
				senderHelp:
					"Adds a second output, “sender”: the ID and definition of the process that sent the event, or nothing for an event from a person or from this process itself.",
			},

			lifecycle: {
				title: "Dataobject lifecycle event",
				filters: "Filters",
				kinds: {
					CREATED: "On start",
					LOADED: "On load",
					CREATED_OR_LOADED: "On start or load",
					REMOVED: "On remove",
					PROCESS_CHANGED_DATA: "On process data change",
					DATAOBJECT_CHANGED_DATA: "On dataobject data change",
				},
				hints: {
					CREATED:
						"When the dataobject is made while the process runs or the process starts fresh. Not after a resume: nothing is made then.",
					LOADED: "When the dataobject comes back from the saved state of a process that continues where it stopped. Never on a fresh start.",
					CREATED_OR_LOADED:
						"Every time the dataobject comes into being, whether the process started fresh or continued. The one for anything a process opens and has to open again — a voicebot, a “SIP: Call Events” action.",
					REMOVED: "When the dataobject is removed.",
					PROCESS_CHANGED_DATA:
						"On every change to the data of any dataobject of the process. What a resume puts back is not a change. Carries the change.",
					DATAOBJECT_CHANGED_DATA:
						"On every change to the data of this activity's dataobject and the dataobjects embedded in it. Carries the change.",
				},
			},

			code: {
				preview: "Code preview",
				title: "Code",
				edit: "Edit code (1 line) | Edit code ({count} lines)",
				dialogTitle: "Action code",
				clientScriptDialogTitle: "Client Script",
				clientScriptRisk:
					"Runs in each operator's own browser window, with their access. Only write code you would run yourself.",
				reviewChanges: "Review proposed changes — highlighted rows changed",
				decline: "Decline",
				accept: "Accept",
				assistant: "AI Assistant",
				resizeAssistant: "Resize the AI assistant",
				assistantHint:
					"Describe what this action should do — the assistant edits only the code, and you can accept or decline its changes.",
				promptPlaceholder: "e.g. Build a greeting from the input and broadcast it",
				send: "Send",
				thinking: "Thinking…",
				reasoning: "Reasoning",
				noChanges: "No changes needed.",
				updated: "Updated the code.",
				failed: "Request failed.",
				notConfigured: "The AI assistant is not configured on this server.",
			},

			question: {
				openAnswer: "{name}, {type}, feeds {target}. Open its answer",
				openUnconnectedAnswer: "{name}, {type}, unconnected. Open its answer",
				body: "Question body",
				bodyPlaceholder: "Enter the question text…",
				bodyHint:
					"Markdown. Values stay current while the question is shown, unless the text is frozen.",
				persist:
					"Keep shown once answered — it is then locked, except for answers marked editable",
				removeWhenAssigned: "Remove once its answers are stored",
				removal: {
					title: "Removal",
					assignedHelp:
						"As soon as every attribute this question's answers write holds a value — from this question or from anywhere else. 0 and false count; empty text does not.",
					waitsFor: "Waits for: {attributes}",
					noneAssigned:
						"None of its answers writes an attribute, so this has nothing to wait for.",
					conditionsTitle: "Remove when",
					conditionsNone: "No condition removes it.",
					dialogNote: "Remove the question as soon as…",
				},
				output: "Answer {n}",
				outputTypeHint:
					"The type this answer carries. If it writes an attribute, the attribute decides it.",
				captions: {
					validation: "Validation",
					target: "Answer goes to",
					text: "Text",
					defaultValue: "Default",
					voice: "Voice and suggestions",
					behaviour: "Behaviour",
				},
				answers: "Answers",
				addAnswer: "Add answer",
				removeAnswer: "Remove answer",
				reorderAnswer: "Drag to reorder",
				kind: "Kind",
				name: {
					empty: "Every answer needs a name — it is the answer's key. Saved once it has one.",
					duplicate:
						"Another answer of this question has this name. Saved once each has its own.",
				},
				keyPlaceholder: "name (key)",
				labelPlaceholder: "What the person is asked",
				datatype: "Answer type",
				datatypePlaceholder: "Select a type…",
				datatypeFromAttribute: "Offers the values of {type}, the attribute's type.",
				kindFromAttribute: "Set by the attribute the answer writes: {type}.",
				datatypeUseAttribute: "From the attribute ({type})",
				display: {
					label: "Shown as",
					dropdown: "Dropdown",
					buttons: "Buttons",
				},
				values: {
					label: "Values",
					all: "All values",
					allHint: "Offers every value of this type, including any added to it later.",
					subset: "Only some",
					selected: "{selected} of {total}",
					search: "Filter values…",
					noMatches: "No values match.",
					noneTicked:
						"Nothing ticked yet — until you tick something, the whole type is offered.",
					coversEverything:
						"Every value is ticked. Same as “All values” today, but values added to the type later will not be offered.",
					groups: {
						offered: "Offered",
						notOffered: "Not offered",
					},
					bulk: {
						all: "Tick all",
						none: "Tick none",
						invert: "Invert",
					},
				},
				presentation: {
					hint: "Drag to set the order. A colour or image set here applies to this question only; dimmed ones are the type's.",
					hintFiltered:
						"Clear the filter to drag. A colour or image set here applies to this question only; dimmed ones are the type's.",
					resetOrder: "Reset order",
					color: "Colour of {value}",
					clearColor: "Use the type's colour",
					reset: "Use the type's colour and image",
					image: {
						add: "Use an image of its own",
						replace: "Replace image",
						remove: "Use the type's image",
					},
				},
				onClickPlaceholder: "JS action on click (optional)",
				placeholderPlaceholder: "Shown in the empty field",
				descriptionPlaceholder: "Help shown under the field",
				validation: {
					rule: "Rule {n}",
					noChecks: "No check yet.",
					dialogNote: "An answer is accepted when this holds.",
					message: "Message when refused",
					messagePlaceholder: "Shown when this rule refuses an answer",
					notKept: "A rule needs a check before it is saved.",
					noMessage:
						"No message: an answer this rule refuses is turned down without a word.",
					moveUp: "Move rule up",
					moveDown: "Move rule down",
					remove: "Remove rule",
					none: "No rules — any answer is accepted.",
					add: "Add rule",
				},
				fields: {
					label: "Label",
					placeholder: "Placeholder",
					description: "Description",
					voiceHint: "Voice hint",
				},
				voiceHintPlaceholder: "How a voicebot should ask or confirm this field",
				suggestionSource: {
					label: "Suggestions come from",
					auto: "Automatic",
					provider: "The provider only",
					llm: "The LLM",
					none: "Nowhere",
					hint: "Automatic: a dataobject input whose type has a registered provider (Location) is answered by it, anything else by the LLM. The provider only never falls back to the LLM — no provider, no suggestion.",
				},
				defaultValue: "default value (optional)",
				defaultExpression: "default value (TS expr, optional)",
				historySize: "suggestion history size (optional, unbounded if blank)",
				disableSuggestions: "Disable suggestions",
				skipConfirmation: "Skip confirmation",
				editableAfterCompletion: "Editable after completion",
				editableAfterCompletionHint:
					"Once every answer has been given, the question is recorded in its history and locked. An answer with this checked still accepts new values, and can be edited from the history.",
				silentHint:
					"When checked, a voicebot sets the value of this answer without verbally confirming it first. Only affects voicebot behaviour.",
				modeNormal: "Only its output",
				modeAttributeAssigned: "An attribute",
				modeAttributeAssignedHint:
					"The answer is also written straight into the chosen attribute, through an Attribute Writer that is added for it automatically. A Select answer in this mode also filters its options live, offering only the values the attribute's axioms currently allow.",
				attributePlaceholder: "Select target attribute…",
				dataobjectPlaceholder: "Select dataobject…",
				requiredFields: {
					caption: "Required fields",
					hint: "An answer without one of them is partial: it is taken, and the question stays open until the rest arrives.",
				},
				locationRegion: {
					caption: "Region",
					hint: "Where the location service looks for this answer. A name must be written exactly as the service has it, such as Saarland or Thueringen; a name it does not know restricts nothing.",
					inherit: "The process's region",
					anywhere: "All of Germany",
					named: "A region",
					placeholder: "Saarland",
				},
				kinds: {
					text: "Text",
					number: "Number",
					boolean: "Yes/No",
					select: "Select",
					button: "Button",
					dataobject: "Dataobject",
				},
			},

			ports: {
				inputs: "Inputs",
				outputs: "Outputs",
				addType: "Add type…",
				untyped: "untyped",
				unconnected: "unconnected",
				disconnect: "Disconnect",
				removeInput: "Remove input",
				reserved: "Built in — this input cannot be removed or changed.",
				dismissed: "dismissed",
				removeOutput: "Remove output",
				outputNamePlaceholder: "name…",
				outputNameHint: "Optional output name (descriptive only)",
				outputNameEmitHint: "The name the script uses to emit on this output",
				unnamedOutputs:
					"An output without a name cannot be reached: the script emits on an output by its name.",
				repeatedOutputNames:
					"More than one output is named {names} — the script reaches an output by its name, so each needs its own.",
				changeType: "{type} — click to change type",
				connectedHint: "Connected — disconnect its input(s) to change the type",
				inputConnectedHint: "Connected — disconnect this input to change its type",
				addControl: "Add control…",
				// What firing a control input does: its tooltip, and its row in the menu that adds it.
				controls: {
					start: "Starts the action",
					stop: "Stops the action",
					hangup: "Ends the call",
					transfer:
						"Hands the call on to the target that arrives, or else to the transfer target",
					call: "Dials the target that arrives, from within the call",
					reset: "Sets the count back to zero",
					cancel: "Stops it without firing",
				},
			},

			vars: {
				value: "Value",
				numberPlaceholder: "0",
				expressionPlaceholder: '"Hello " + value',
				shortTextPlaceholder: "text",
				envPlaceholder: "ENV_NAME",
				attributePlaceholder: "Select attribute…",
				noConnectedInputs: "no connected inputs",
				unset: "Default",
				unsetDefault: "Default ({value})",
				unsetNone: "Not chosen",
				unsetAnyProcess: "Any process",
				selectPlaceholder: "select…",
				templateKind: "Template",
				expressionOn: "Editing as expression — switch back to plain text",
				expressionOff: 'Edit as expression (e.g. "Hello " + value)',
				// The registry's var specs, by the key their `labelKey` names.
				labels: {
					suggestionValue: "Value",
					poolEntries: "Cards",
					chatRoles: "Role names",
					freezeText: "Freeze text when shown",
					interactableId: "Interactable ID",
					panel: "Panel",
					panelTitle: "Title",
					panelColumn: "Column",
					panelAlign: "Alignment",
					chatTitle: "Title",
					onDismiss: "Removable",
					chatLanguages: "Operators can set each role's language",
					chatComposer: "Operators can write messages",
					chatPrefabs: "Operators can send prefabs",
					chatPlay: "Operators can play speech",
					chatSummary: "Show the summary",
					voiceProvider: "Voice provider",
					voiceGender: "Voice gender",
					voiceInstructions: "Voice instructions",
					body: "Body",
					severity: "Severity",
					color: "Colour",
					icon: "Icon",
					attribute: "Attribute",
					explanation: "Explanation",
					dataobjectId: "Dataobject ID",
					title: "Title",
					address: "Address",
					displayKind: "Display kind",
					sender: "Sender",
					emitImmediately: "Play speech automatically",
					sessionId: "Session ID",
					alternisApiBase: "Alternis URL",
					alternisToken: "Alternis token",
					questionBody: "Body (Markdown)",
					persist: "Persist",
					questionTab: "Tab",
					questionGroup: "Group",
					priority: "Priority",
					tabId: "Tab ID",
					questionId: "Question ID",
					message: "Message",
					level: "Level",
					systemPrompt: "System prompt",
					language: "Language",
					languageLock: "Language lock",
					botRole: "Bot role (Alternis role ID)",
					callerRole: "Caller role (initial guess, learned automatically)",
					showChat: "Show chat",
					llmGatewayUrl: "LLM gateway URL",
					llmModel: "LLM model",
					llmApiKey: "LLM gateway token",
					llmTimeout: "LLM timeout (ms)",
					debugLogging: "Debug logging",
					voiceMode: "Voice mode",
					offPath: "When the caller strays",
					floorPolicy: "Who may speak (floor policy)",
					silenceTimeout: "Silence timeout (ms)",
					reaskMax: "Attempts per question",
					greeting: "Greeting",
					closing: "Closing",
					questionScope: "Question scope (tab)",
					spokenText: "Spoken text",
					questionReaskMax: "Attempts before moving on",
					reaskRephrase: "Rephrase when asking again",
					questionOptional: "Optional — the call can be completed without an answer",
					locationServiceUrl: "Location service URL",
					locationServiceTimeout: "Timeout (ms)",
					locationServiceRegion: "Region",
					locationServicePhone: "Phone number",
					suggestionsEnabled: "Suggestions enabled",
					messageText: "Message text",
					prefabId: "Prefab ID",
					sipNumbers: "Numbers",
					sipAction: "Action",
					callId: "Call ID",
					sipNumber: "Number",
					sipExtension: "Extension",
					sipReference: "Reference",
					sipStatus: "Rejection status",
					alternisIntegration: "Alternis integration",
					bargeIn: "Barge-in",
					bargeInGraceMs: "Barge-in grace (ms)",
					bargeInDuck: "Turn the line down while the caller speaks",
					bargeInMinWords: "Words before the bot stops",
					cueThinking: "Play a thinking sound",
					cueListening: "Play a sound when the caller's turn begins",
					cueMute: "Play sounds when the caller is muted and unmuted",
					transferTarget: "Transfer target",
					locationCallerNumber: "Send the caller's number to the location service",
					pathPrefix: "Only below this path",
					changeKinds: "Only these changes",
					excludeOrigins: "Ignore changes made by",
					dataobjectOperation: "Operation",
					concept: "Dataobject",
					template: "Template",
					embedPath: "Embedded slot",
					relation: "Relation",
					targetId: "Dataobject ID",
					operationData: "Starting data",
					ownerId: "Dataobject to act on (ID)",
					ownerConcept: "Concept of that dataobject",
					interval: "Every (ms)",
					tickLimit: "Stop after (ticks)",
					timeout: "After (ms)",
					retrigger: "When started while running",
					passthrough: "Hand on the start value",
					schedule: "Schedule",
					at: "Date and time",
					timeOfDay: "Time of day",
					weekdays: "On these days",
					timezone: "Time zone",
					missed: "When a time is missed",
					processDefinition: "Process",
					instanceName: "Name of the new process",
					instanceDescription: "Description of the new process",
					restartRule: "Restart rule",
					seed: "Starting dataobjects",
					targetProcessId: "Instance ID",
					targetDefinition: "Every running process of",
					eventChannel: "Event name",
					eventData: "Data",
					processOperation: "Operation",
					controlTarget: "Process ID",
					actors: "People",
					accessOperation: "Operation",
					accessUser: "Person",
					accessGrant: "Access",
					listStatus: "Status",
					listArchived: "Archived",
					listSearch: "Text",
					listCreatedBy: "Started by (user ID)",
					listSpawnedBy: "Started by (process ID)",
					listCreatedFrom: "Created from",
					listCreatedTo: "Created before",
					listUpdatedFrom: "Updated from",
					listUpdatedTo: "Updated before",
					listSort: "Sort by",
					listOrder: "Order",
					listLimit: "At most",
					listOffset: "Skip",
				},
				options: {
					languageLockSoft: "Soft — adapt to caller",
					languageLockHard: "Hard — always this language",
					changeKinds: {
						attribute: "A value written",
						embedded_created: "An embedded dataobject added",
						embedded_removed: "An embedded dataobject removed",
						relation_linked: "A relation linked",
						relation_unlinked: "A relation unlinked",
					},
					changeOrigins: {
						reasoning: "The process itself",
						user: "A person",
						external: "The host or the API",
						axiom_inferred: "An axiom",
					},
					weekdays: {
						mon: "Mon",
						tue: "Tue",
						wed: "Wed",
						thu: "Thu",
						fri: "Fri",
						sat: "Sat",
						sun: "Sun",
					},
					dataobjectOperations: {
						create: "Create a dataobject",
						create_embedded: "Create an embedded dataobject",
						create_related: "Create a related dataobject",
						link_related: "Link a related dataobject",
						unlink_related: "Unlink a related dataobject",
					},
					retrigger: { restart: "Count again from now", ignore: "Leave it running" },
					panelColumn: { left: "Left", middle: "Middle", right: "Right" },
					panelAlign: { top: "Top", bottom: "Bottom" },
					removable: {
						none: "No",
						remove: "Yes, remove it",
						output: "Yes, fire the output",
					},
					schedule: { once: "Once, at a date and time", daily: "Every day, at a time" },
					missed: { skip: "Forget it", fire_once: "Fire once for the newest" },
					restartRules: {
						"by-user": "Only when a person asks",
						"unless-stopped": "Always, unless stopped",
						"on-failure": "When it fails",
						no: "Never",
					},
					accessOperations: {
						grant: "Share",
						revoke: "Stop sharing",
					},
					accessGrants: {
						read: "Can view",
						read_write: "Can view and take part",
					},
					processOperations: {
						stop: "Stop",
						restart: "Restart",
						archive: "Archive",
						unarchive: "Unarchive",
						delete: "Delete",
					},
					processStatuses: {
						starting: "Starting",
						running: "Running",
						stopped: "Stopped",
						failed: "Failed",
						archived: "Archived",
					},
					processSorts: {
						created_at: "Created",
						updated_at: "Last changed",
						name: "Name",
						status: "Status",
						definition: "Definition",
					},
					processOrders: {
						asc: "Ascending",
						desc: "Descending",
					},
				},
			},
		},

		droppedConnections: {
			one: "Removed the connection from {output} to “{node}”: its input {input} takes {inputType}, not {outputType}.",
			many: "Removed {n} connections whose inputs no longer take the type of what fed them.",
		},
		conditions: {
			title: "Conditions",
			add: "Add condition",
			addRule: "Add rule",
			remove: "Remove condition",
			none: "Always executes — no conditions set.",
			removesOnFail:
				"When the action fires and these conditions do not hold, what it is showing is removed, as its remove input would.",
			generatedCode: "Generated code",
			dialogTitle: "Add condition",
			dialogSubtitle: "Run this action only when…",
			if: "if",
			theAnswer: "the answer",
			answerKind: "Subject",
			// Before each rule after the first: a label that follows the group's one operator.
			joinAnd: "and",
			joinOr: "or",
			typeIssue: {
				operator: "This check doesn't fit the input's current type.",
				value: "The value isn't written as the input's current type.",
				input: "The other input no longer compares with this input's type.",
			},
			operatorLabel: "Combine the rules with",
			operatorAnd: "AND",
			operatorOr: "OR",
			edit: "Edit condition",
			editDialogTitle: "Edit condition",
			saveRule: "Save rule",
			subjectKind: "Subject kind",
			valueKind: "Value kind",
			operator: "Operator",
			value: "Value",
			input: "Input",
			comparedInput: "Compared input",
			comparedEnv: "Compared environment variable",
			envName: "Environment variable name",
			fixedValue: "Fixed value",
			attributePlaceholder: "attribute…",
			inputPlaceholder: "input…",
			envPlaceholder: "VAR_NAME",
			valuePlaceholder: "value…",
			choosePlaceholder: "choose…",
			emptyPlaceholder: "—",
			noInputs: "no inputs",
			noCompatibleInputs: "no compatible inputs",
			// Suffix of the canvas overflow marker: "… +2 more".
			more: "more",
			sources: {
				attr: "Attribute",
				inp: "Input",
				env: "Env var",
				lit: "Value",
			},
			kinds: {
				string: "text",
				number: "number",
				boolean: "yes / no",
				array: "list",
				enum: "option",
			},
			operators: {
				isDefined: "is defined",
				isNotDefined: "is not defined",
				isEmpty: "is empty",
				isNotEmpty: "is not empty",
				isEqual: "equals",
				isNotEqual: "not equals",
				isGreaterThan: ">",
				isLessThan: "<",
				isGreaterThanOrEqual: "≥",
				isLessThanOrEqual: "≤",
				contains: "contains",
			},
		},
	},
};
